/**
 * CollectionAndDetailView2 Component
 *
 * Combines PhotoCollection2 with DetailView2 in a resizable split-pane layout.
 * Coordinates selection state between collection and detail views.
 *
 * Features:
 * - Four positioning modes (top, bottom, left, right)
 * - Resizable panels with constraints (20-60% collection, min 40% detail)
 * - Automatic photo filtering by selection (max 9 for detail view)
 * - localStorage persistence for panel sizes
 * - Dynamic overflow handling based on layout's scroll direction
 * - Header and Footer slots with fixed/scroll positioning (#102)
 *
 * @module CollectionAndDetailView2
 */

import { Children, isValidElement } from 'react';
import { cn } from '@/lib/utils';
import { SplitPane, Panel, PanelResizeHandle } from '@/components/layout/SplitPane';
import { PhotoCollection2 } from '@/components/PhotoCollection2';
import { DetailView2 } from '@/components/DetailView2';
import type {
  CollectionAndDetailView2Props,
  CollectionHeaderProps,
  CollectionFooterProps,
  SlotPosition,
} from './CollectionAndDetailView2.types';

// ============================================
// Header Sub-Component (#102)
// ============================================

/**
 * Header sub-component for CollectionAndDetailView2
 *
 * Use this to add header content to the collection area.
 * The `position` prop controls whether it's fixed or scrolls with content.
 *
 * @example
 * ```tsx
 * <CollectionAndDetailView2.Header position="fixed">
 *   <Toolbar />
 * </CollectionAndDetailView2.Header>
 * ```
 */
const CollectionHeader: React.FC<CollectionHeaderProps> = ({
  children,
  position = 'scroll',
  className,
}) => {
  // This component is a marker - actual rendering happens in PhotoCollection2
  // The div wrapper preserves className and allows inspection
  return (
    <div className={className} data-slot="header" data-position={position}>
      {children}
    </div>
  );
};
CollectionHeader.displayName = 'CollectionAndDetailView2.Header';

// ============================================
// Footer Sub-Component (#102)
// ============================================

/**
 * Footer sub-component for CollectionAndDetailView2
 *
 * Use this to add footer content to the collection area.
 * The `position` prop controls whether it's fixed or scrolls with content.
 *
 * @example
 * ```tsx
 * <CollectionAndDetailView2.Footer position="fixed">
 *   <ActionBar />
 * </CollectionAndDetailView2.Footer>
 * ```
 */
const CollectionFooter: React.FC<CollectionFooterProps> = ({
  children,
  position = 'scroll',
  className,
}) => {
  // This component is a marker - actual rendering happens in PhotoCollection2
  // The div wrapper preserves className and allows inspection
  return (
    <div className={className} data-slot="footer" data-position={position}>
      {children}
    </div>
  );
};
CollectionFooter.displayName = 'CollectionAndDetailView2.Footer';

// ============================================
// Slot Extraction Logic (#102)
// ============================================

interface ExtractedSlots {
  fixedHeader: React.ReactNode;
  scrollHeader: React.ReactNode;
  scrollFooter: React.ReactNode;
  fixedFooter: React.ReactNode;
}

/**
 * Extract Header and Footer slots from children
 * Categorizes by component type and position prop
 */
function extractSlots(children: React.ReactNode): ExtractedSlots {
  const slots: ExtractedSlots = {
    fixedHeader: null,
    scrollHeader: null,
    scrollFooter: null,
    fixedFooter: null,
  };

  Children.toArray(children).forEach((child) => {
    if (!isValidElement(child)) return;

    // Check if it's a Header or Footer by comparing component type
    if (child.type === CollectionHeader) {
      const position: SlotPosition = child.props.position || 'scroll';
      if (position === 'fixed') {
        slots.fixedHeader = child;
      } else {
        slots.scrollHeader = child;
      }
    } else if (child.type === CollectionFooter) {
      const position: SlotPosition = child.props.position || 'scroll';
      if (position === 'fixed') {
        slots.fixedFooter = child;
      } else {
        slots.scrollFooter = child;
      }
    }
    // Non-Header/Footer children are ignored
  });

  return slots;
}

// ============================================
// Main Component
// ============================================

/**
 * CollectionAndDetailView2 - Composite component for collection + detail view
 *
 * Layout Behavior:
 * - Collection panel: 20-60% of space (default 30% vertical, 20% horizontal)
 * - Detail panel: minimum 40% of space
 * - Panel sizes persist to localStorage
 *
 * Selection Behavior:
 * - Passes selectedIds and onSelectionChange directly to PhotoCollection2
 * - Filters photos by selectedIds before passing to DetailView2
 * - Maximum 9 photos passed to DetailView2 (first 9 if more selected)
 *
 * Scroll Handling:
 * - Reads scrollDirection from the layout's configuration in layoutConfigs registry
 * - Horizontal scroll layouts (e.g., horizontal filmstrip) get overflow-x-auto
 * - Vertical scroll layouts (e.g., grid, magazine, vertical feed) get overflow-y-auto
 * - Extensible: new layouts automatically inherit correct overflow behavior
 *
 * Header/Footer Slots (#102):
 * - Use CollectionAndDetailView2.Header and CollectionAndDetailView2.Footer
 * - position="fixed": Outside scroll container, always visible
 * - position="scroll": Inside scroll container, scrolls with content
 * - Supported layouts: grid, magazine, vertical
 * - Unsupported layout (horizontal): logs warning, scroll slots ignored, fixed slots still rendered
 *
 * @example
 * ```tsx
 * <CollectionAndDetailView2
 *   photos={photos}
 *   collectionPosition="bottom"
 *   layout="magazine"
 *   selectionMode="multiple"
 *   selectedIds={selectedIds}
 *   onSelectionChange={setSelectedIds}
 * >
 *   <CollectionAndDetailView2.Header position="fixed">
 *     <Toolbar />
 *   </CollectionAndDetailView2.Header>
 *   <CollectionAndDetailView2.Footer position="scroll">
 *     <EndOfCollection />
 *   </CollectionAndDetailView2.Footer>
 * </CollectionAndDetailView2>
 * ```
 */
const CollectionAndDetailView2Component: React.FC<CollectionAndDetailView2Props> = ({
  collectionPosition,
  photos,
  selectedIds = [],
  layout = 'grid',
  className,
  hideDetail = false,
  collectionDefaultSize: collectionDefaultSizeProp,
  autoSaveId,
  initialZoomLevel,
  onZoomChange,
  children,
  ...photoCollectionProps
}) => {
  // Extract slots from children (#102)
  const slots = extractSlots(children);

  // Extract slots from children and pass to PhotoCollection2. PhotoCollection2 will handle scroll slot filtering based on layout compatibility
  // PhotoCollection2 will render fixed slots for all layouts, but only render
  // scroll slots for vertical-scroll layouts (grid, magazine, vertical)
  const slotProps = {
    fixedHeader: slots.fixedHeader,
    scrollHeader: slots.scrollHeader,
    scrollFooter: slots.scrollFooter,
    fixedFooter: slots.fixedFooter,
  };

  // Determine SplitPane direction based on collection position
  const direction = collectionPosition === 'top' || collectionPosition === 'bottom'
    ? 'vertical'
    : 'horizontal';

  // Resize handle styling based on split direction
  // Vertical split (top/bottom): horizontal handle needs h-px (thin height) + w-full (full width)
  // Horizontal split (left/right): vertical handle needs w-px (thin width) + h-full (full height)
  const resizeHandleClasses = direction === 'vertical'
    ? 'h-px w-full bg-border'
    : 'w-px h-full bg-border';

  // Determine rendering order based on position
  const collectionFirst = collectionPosition === 'top' || collectionPosition === 'left';

  // Filter photos for detail view (only selected, max 9)
  const selectedPhotos = photos
    .filter((photo) => selectedIds.includes(photo.photoData.id))
    .slice(0, 9);

  // Default sizes based on layout direction (can be overridden via prop)
  const defaultSizeFallback = direction === 'vertical' ? 30 : 20;
  const collectionDefaultSize = collectionDefaultSizeProp ?? defaultSizeFallback;

  // Note: Panel sizes are automatically saved to localStorage by react-resizable-panels
  // using the autoSaveId key. Each view mode has its own saved layout.

  // Collection panel - let PhotoCollection2 handle its own scrolling internally
  // We just provide a sized container; PhotoCollection2 manages overflow based on layout
  const collectionPanel = (
    <Panel
      defaultSize={collectionDefaultSize}
      minSize={10}
      maxSize={60}
      className="overflow-hidden"
    >
      <div className="w-full h-full min-w-0 min-h-0">
        <PhotoCollection2
          photos={photos}
          selectedIds={selectedIds}
          layout={layout}
          {...photoCollectionProps}
          {...slotProps}
        />
      </div>
    </Panel>
  );

  // Detail panel - no defaultSize, will automatically take remaining space
  const detailPanel = (
    <Panel
      minSize={40}
      className="overflow-hidden"
    >
      <DetailView2
        photos={selectedPhotos}
        initialZoomLevel={initialZoomLevel}
        onZoomChange={onZoomChange}
      />
    </Panel>
  );

  // Feature 043: When hideDetail is true, only show collection (for edit mode)
  if (hideDetail) {
    return (
      <div className={cn('w-full h-full min-h-0', className)}>
        <PhotoCollection2
          photos={photos}
          selectedIds={selectedIds}
          layout={layout}
          {...photoCollectionProps}
          {...slotProps}
        />
      </div>
    );
  }

  // Normal mode: Show split pane with collection and detail
  const splitPaneKey = autoSaveId ?? `collection-detail-${collectionPosition}`;

  return (
    <div className={cn('w-full h-full min-h-0', className)}>
      <SplitPane
        key={splitPaneKey}
        direction={direction}
        autoSaveId={splitPaneKey}
      >
        {collectionFirst ? (
          <>
            {collectionPanel}
            <PanelResizeHandle className={resizeHandleClasses} />
            {detailPanel}
          </>
        ) : (
          <>
            {detailPanel}
            <PanelResizeHandle className={resizeHandleClasses} />
            {collectionPanel}
          </>
        )}
      </SplitPane>
    </div>
  );
};

CollectionAndDetailView2Component.displayName = 'CollectionAndDetailView2';

// ============================================
// Export with Static Properties (#102)
// ============================================

/**
 * CollectionAndDetailView2 with Header and Footer sub-components
 *
 * @example
 * ```tsx
 * <CollectionAndDetailView2 photos={photos} collectionPosition="top">
 *   <CollectionAndDetailView2.Header position="fixed">
 *     <Toolbar />
 *   </CollectionAndDetailView2.Header>
 * </CollectionAndDetailView2>
 * ```
 */
export const CollectionAndDetailView2 = Object.assign(CollectionAndDetailView2Component, {
  Header: CollectionHeader,
  Footer: CollectionFooter,
});
